import { useState, useRef, useCallback } from 'react';
import { RefreshCw, Trash2, Undo2, ChevronDown, Check, FileText, FileCode } from 'lucide-react';
import { useEditorStore } from '../store/editor-store';
import type { ViewMode } from '../types';

const VIEW_MODES: { value: ViewMode; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'github', label: 'GitHub' },
  { value: 'email', label: 'Email' },
  { value: 'raw', label: 'Raw' },
];

function htmlToPlainText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function CopySourceBtn({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} className="btn-native" title="Copy source code as plain text">
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <FileText className="w-4 h-4" />}
      {copied ? 'Copied!' : 'Copy Source'}
    </button>
  );
}

function CopyRichBtn({ html }: { html: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      const plainText = htmlToPlainText(html);

      // Use modern Clipboard API with both HTML and plain text
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that don't support ClipboardItem
      await navigator.clipboard.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button onClick={handleCopy} className="btn-native" title="Copy as rich text (paste into Word, Gmail, etc.)">
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <FileCode className="w-4 h-4" />}
      {copied ? 'Copied!' : 'Copy Rich'}
    </button>
  );
}

export function Toolbar() {
  const { source, renderedHtml, liveSync, viewMode, lastSource, toggleLiveSync,
          setViewMode, hardRefresh, cleanSlate, undoClean, isRefreshing } = useEditorStore();
  const [isHoldingClean, setIsHoldingClean] = useState(false);
  const cleanTimeoutRef = useRef<number | null>(null);

  const handleCleanStart = useCallback(() => {
    setIsHoldingClean(true);
    cleanTimeoutRef.current = window.setTimeout(() => {
      cleanSlate();
      setIsHoldingClean(false);
    }, 500);
  }, [cleanSlate]);

  const handleCleanEnd = useCallback(() => {
    if (cleanTimeoutRef.current) {
      clearTimeout(cleanTimeoutRef.current);
      cleanTimeoutRef.current = null;
    }
    setIsHoldingClean(false);
  }, []);

  return (
    <div className="flex items-center justify-between px-12 py-5 bg-gradient-to-b from-[#f5f5f7] to-[#e8e8ed] border-b border-gray-300">
      <div className="flex items-center gap-6">
        <h1 className="text-xl font-semibold text-gray-800 tracking-tight pr-2">html2html</h1>

        <div className="flex items-center gap-3 pl-4 border-l border-gray-300">
          <span className="text-sm font-medium text-gray-600">Live</span>
          <button
            onClick={toggleLiveSync}
            className={`toggle-switch ${liveSync ? 'active' : ''}`}
            aria-label="Toggle live sync"
          />
        </div>

        <div className="flex items-center gap-3 pl-4 border-l border-gray-300">
          <button onClick={hardRefresh} className="btn-native">
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <button
            onMouseDown={handleCleanStart}
            onMouseUp={handleCleanEnd}
            onMouseLeave={handleCleanEnd}
            className={`btn-native ${isHoldingClean ? '!bg-red-100 !border-red-300' : ''}`}
          >
            <Trash2 className="w-4 h-4" />
            {isHoldingClean ? 'Hold...' : 'Clean'}
          </button>

          {lastSource && (
            <button onClick={undoClean} className="btn-native !bg-amber-50 !border-amber-300">
              <Undo2 className="w-4 h-4" />
              Undo
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 pl-4 border-l border-gray-300">
          <CopySourceBtn content={source} />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Preview</span>
          <div className="relative">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
              className="btn-native !pr-9 appearance-none cursor-pointer"
            >
              {VIEW_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </label>
        <div className="pl-4 border-l border-gray-300">
          <CopyRichBtn html={renderedHtml} />
        </div>
      </div>
    </div>
  );
}
